import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Zone {
  label: string;
  radiusKm: number;
  color: string;
}

interface ZoneMapProps {
  lat: number;
  lng: number;
  zones: Zone[];
  className?: string;
}

export function ZoneMap({ lat, lng, zones, className }: ZoneMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 10,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: true,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 16,
    }).addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    const circles: L.Circle[] = [];

    const sorted = [...zones].sort((a, b) => b.radiusKm - a.radiusKm);
    for (const zone of sorted) {
      const circle = L.circle([lat, lng], {
        radius: zone.radiusKm * 1000,
        color: zone.color,
        fillColor: zone.color,
        fillOpacity: 0.08,
        weight: 2,
        dashArray: zone === sorted[0] ? '8 4' : undefined,
      }).addTo(map);
      circle.bindTooltip(zone.label, { permanent: false, direction: 'center' });
      circles.push(circle);
    }

    L.circleMarker([lat, lng], {
      radius: 5,
      color: '#FF993B',
      fillColor: '#FF993B',
      fillOpacity: 1,
      weight: 2,
    }).addTo(map);

    if (circles.length > 0) {
      const outermost = circles[0];
      map.fitBounds(outermost.getBounds().pad(0.1));
    }

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [lat, lng, zones]);

  return <div ref={containerRef} className={className ?? 'w-full h-64 rounded-xl overflow-hidden'} />;
}
